import {computed, observable, override, makeObservable} from "mobx";
import {BaseModuleState} from "../../../infrastructure/models/base-module-state";
import {BaseMobxState} from "../../../infrastructure/mobx/base-mobx-state";
import {NavigationManager} from "../../../core/managers/navigation-manager";
import {AppStore} from "../../../core/app/app-store";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {DeviceViewModel} from "../../../core/models/device.view-model";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {PropositionBetsWidgetConfig} from "../proposition-bets-widget.config";
import type {PropositionBetsDataSource} from "../../../domain/bets/proposition/models/proposition-bets.data-source";
import {PropositionBetsViewModel} from "../../../domain/bets/proposition/models/proposition-bets.view-model";
import {BookmakerViewModel} from "../../../domain/common/bookmaker/models/bookmaker-view-model";
import type {BookmakerDomainModel} from "../../../domain/common/bookmaker/models/bookmaker-domain-model";
import {GAME_STATUSES} from "../../../core/app/consts";
import {ROUTE_NAMES} from "../../../core/consts";
import {GameCenterLinkModel} from "../../../containers/game-center-container/models/game-center.link-model";
import {PROPOSITION_BETS} from "../proposition-bets-widgets.consts";
import {PropositionBetsDetailsDisplayModel} from "../../../core/models/proposition-bets-details/proposition-bets.details.display-model";
import {GameCenterHashParamsModel} from "../../../containers/game-center-container/models/game-center.hash-params-model";

export class PropositionBetsWidgetState extends BaseModuleState {
  dataSource: PropositionBetsDataSource;
  @observable bets: Array<PropositionBetsViewModel>;
  @observable popupDisplay: PropositionBetsDetailsDisplayModel;

  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, PropositionBetsWidgetConfig];

  constructor(
    navigationManager: NavigationManager,
    appStore: AppStore,
    logger: LoggerUtils,
    datetimeUtils: DatetimeUtils,
    device: DeviceViewModel,
    termsUtils: TermsUtils,
    config: PropositionBetsWidgetConfig,
  ) {
    super(navigationManager, appStore, logger, datetimeUtils, device, termsUtils, config);

    this.popupDisplay = new PropositionBetsDetailsDisplayModel();

    makeObservable(this);
  }

  @override get isPopulated(): Boolean {
    return this.props.isPreview ? this.dataSource?.propositionBets?.length > 0 : true;
  }

  @computed get isVisible(): Boolean {
    const isVisible: Boolean = this.popupDisplay?.athleteId > 0 && this.popupDisplay?.lineTypeId > 0 && this.popupDisplay?.gameId > 0;

    return isVisible;
  }

  @computed get previewBets(): Array<PropositionBetsViewModel> {
    return this.bets?.slice(0, PROPOSITION_BETS.previewBets);
  }

  @computed get game(): String {
    return this.dataSource.games?.first()
  }

  @computed get statusGroup(): String {
    return this.game?.statusGroup;
  }

  @computed get isScheduled(): Boolean {
    return this.statusGroup === GAME_STATUSES.scheduled;
  }

  @computed get isLive(): Boolean {
    return this.statusGroup === GAME_STATUSES.started;
  }

  @computed get isEnded(): Boolean {
    return this.statusGroup === GAME_STATUSES.ended;
  }

  @computed get isGameRoute() {
    return this.currentLinkModel?.name === ROUTE_NAMES.game
  }

  get gameCenterLinkModel(): GameCenterLinkModel {
    let linkModel: GameCenterLinkModel = null;

    if(this.game?.id > 0) {
      const hashParams: GameCenterHashParamsModel = new GameCenterHashParamsModel({id: this.game.id});
      linkModel = new GameCenterLinkModel(this.game.matchupId, this.game.sport.nameForURL, this.game.competition.nameForURL, this.game.nameForUrl, hashParams, this.game.isFollow);
    }

    return linkModel;
  }

  @computed get bookmaker(): BookmakerViewModel {
    const bookmakerDM: BookmakerDomainModel = this.dataSource.bookmakers?.first()
    return bookmakerDM ? new BookmakerViewModel(bookmakerDM) : null
  }

  @computed get title(): Boolean {
    return this.props.isPreview ? this.terms.WEB_PROPS_FILTER_PROPS : null;
  }

  @computed get shouldRenderEmptyState(): Boolean {
    return !this.props.isPreview && !this.bets
  }

  @computed get shouldRenderPreview(): Boolean {
    return this.props.isPreview;
  }

  @computed get sportTypeId(): Number {
    return this.appState.context.sportType?.id;
  }

  @computed get topBookmakerId(): Number {
    return this.appState.topBookmakerId;
  }
}
